/**
 * Featured collections
 * - <em-collection-tabs>: accessible tab list switching between collection panels.
 * - <em-scroll-progress aria-controls="ID">: thin bar reflecting how far a list is scrolled.
 */

const PANEL_FADE_MS = 150;

class EmCollectionTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = Array.from(this.querySelectorAll('button[role="tab"]'));
    if (this.tabs.length < 2) return;

    this.controller = new AbortController();
    const { signal } = this.controller;
    this.selectedIndex = Math.max(0, this.tabs.findIndex((tab) => tab.getAttribute('aria-selected') === 'true'));

    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index), { signal });
    });

    this.addEventListener('keydown', (event) => this.onKeydown(event), { signal });

    if (window.Shopify?.designMode) {
      this.addEventListener(
        'shopify:block:select',
        (event) => {
          const index = this.tabs.indexOf(event.target.closest?.('[role="tab"]'));
          if (index >= 0) this.select(index, { instant: true });
        },
        { signal }
      );
    }
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  panelFor(tab) {
    return document.getElementById(tab.getAttribute('aria-controls') || '');
  }

  onKeydown(event) {
    const current = this.tabs.indexOf(document.activeElement);
    if (current === -1 || (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight')) return;

    event.preventDefault();
    const step = event.key === 'ArrowRight' ? 1 : -1;
    const index = (current + step + this.tabs.length) % this.tabs.length;
    this.select(index);
    this.tabs[index].focus();
  }

  async select(index, { instant = false } = {}) {
    if (index === this.selectedIndex || !this.tabs[index]) return;

    const fromPanel = this.panelFor(this.tabs[this.selectedIndex]);
    const toPanel = this.panelFor(this.tabs[index]);
    this.selectedIndex = index;
    const token = (this.token = (this.token || 0) + 1);

    this.tabs.forEach((tab, position) => {
      const selected = position === index;
      tab.setAttribute('aria-selected', selected ? 'true' : 'false');
      if (selected) tab.removeAttribute('tabindex');
      else tab.tabIndex = -1;
    });

    const tab = this.tabs[index];
    this.scrollTo({
      left: tab.offsetLeft - this.clientWidth / 2 + tab.clientWidth / 2,
      behavior: instant ? 'auto' : 'smooth',
    });

    const viewAll = this.nextElementSibling;
    if (viewAll?.tagName === 'A' && toPanel?.dataset.url) viewAll.setAttribute('href', toPanel.dataset.url);

    if (!fromPanel || !toPanel) return;

    const animate = !instant && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const timing = { duration: PANEL_FADE_MS, easing: 'ease', fill: 'forwards' };

    if (animate) {
      const leave = fromPanel.animate(
        [
          { opacity: 1, transform: 'translateY(0)' },
          { opacity: 0, transform: 'translateY(10px)' },
        ],
        timing
      );
      await leave.finished.catch(() => {});
      leave.cancel();
      if (token !== this.token) return;
    }

    this.querySelectorAll('[role="tab"]').forEach((otherTab) => {
      const panel = this.panelFor(otherTab);
      if (panel) panel.hidden = panel !== toPanel;
    });

    if (animate) {
      const enter = toPanel.animate(
        [
          { opacity: 0, transform: 'translateY(10px)' },
          { opacity: 1, transform: 'translateY(0)' },
        ],
        timing
      );
      await enter.finished.catch(() => {});
      enter.cancel();
    }
  }
}

class EmScrollProgress extends HTMLElement {
  connectedCallback() {
    this.list = document.getElementById(this.getAttribute('aria-controls') || '');
    if (!this.list) return;

    this.controller = new AbortController();
    this.list.addEventListener('scroll', () => this.update(), { signal: this.controller.signal, passive: true });
    this.resizeObserver = new ResizeObserver(() => this.update());
    this.resizeObserver.observe(this.list);
    this.update();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.resizeObserver?.disconnect();
  }

  update() {
    const { scrollWidth, clientWidth, scrollLeft } = this.list;
    const ratio = scrollWidth > 0 ? Math.min(1, (clientWidth + Math.abs(scrollLeft)) / scrollWidth) : 1;
    this.style.setProperty('--em-progress', ratio.toFixed(4));
  }
}

if (!customElements.get('em-collection-tabs')) {
  customElements.define('em-collection-tabs', EmCollectionTabs);
}

if (!customElements.get('em-scroll-progress')) {
  customElements.define('em-scroll-progress', EmScrollProgress);
}
